import uuidV4 from 'uuid/v4';

import { experiment } from 'wanderu-analytics';
import appSettings from 'wanderu-config';
import { splitTestSelectors } from 'wanderu-ducks-split-test';
import {
    selectLocale,
    selectCurrency,
} from 'wanderu-i18n';
import Logger from 'wanderu-logger';
import { SnowplowService } from 'wanderu-service-analytics';
import { OrdersService, TixService } from 'wanderu-service-wapi';

import {
    actions,
    namespace,
    reducer,
    selectors,
} from './base';
import {
    CONTACT_INFO_SECTION_ID,
    ACCOUNT_PASSWORD_ID,
} from '../../../constants';
import {
    actions as bookingActions,
    selectors as bookingSelectors,
} from '../../appstate';
import { orderIdReceived } from '../../ordersSlice';

const logger = Logger.getLogger('wanderu-app-booking:fetchBookingForm');

async function fetchLocalityData(url) {
    const response = await fetch(url);
    return response.json();
}

const passwordField = {
    id: ACCOUNT_PASSWORD_ID,
    isValid: true,
    label: 'password',
    type: 'password',
    validation: {
        isRequired: false,
        serverSide: false,
    },
};

function addExtraFieldsHelper(formFields) {
    return formFields.reduce((fields, field) => {
        if (field.fields) {
            const fieldsGroup = [
                ...fields,
                {
                    ...field,
                    fields: addExtraFieldsHelper(field.fields),
                },
            ];

            if (field.id === CONTACT_INFO_SECTION_ID) {
                fieldsGroup.push(passwordField);
            }

            return fieldsGroup;
        }

        return [
            ...fields,
            field,
        ];
    }, []);
}

function addExtraFields(formFields) {
    const fieldsCopy = addExtraFieldsHelper(formFields);

    return fieldsCopy;
}

function fetchFormAndSummary(opts) {
    return async (dispatch, getState) => {
        const working = selectors.selectWorking(getState());
        const success = selectors.selectSuccess(getState());
        if (working || success) {
            return;
        }

        dispatch(actions.startWork());

        let error;
        let actionMeta;
        try {
            const bookingSessionId = uuidV4();
            dispatch(bookingActions.setBookingSessionId(bookingSessionId));
            const locale = selectLocale(getState());
            const currency = selectCurrency(getState());
            const {
                tripIds,
                itineraryId,
                seats,
                wheelchairs,
                selectedFaresMap,
                tokenMap = {},
            } = bookingSelectors.selectBookingInfo(getState());

            const dropInVariant = splitTestSelectors.selectTestVariant(getState(), 'adyenDropIn');
            const formVersionMap = {
                no_dropin: '2.4.0',
            };
            const chooseFormVersionForTest = () => {
                if (formVersionMap[dropInVariant]) {
                    return formVersionMap[dropInVariant];
                }

                return '2.5.0';
            };
            const formVersion = chooseFormVersionForTest();

            const body = {
                accessibility: {
                    wheelchairs,
                },
                booking_session_id: bookingSessionId,
                cacheTokens: Object.values(tokenMap),
                currency,
                itinerary_id: itineraryId,
                locale,
                meta: {
                    form_version: formVersion,
                    mobile: appSettings.env.isClient
                        ? window.matchMedia('(max-width: 980px)').matches
                        : undefined,
                },
                passengers: seats,
                selected_fares: selectedFaresMap,
                trip_ids: tripIds,
            };

            const variant = experiment?.variant('orders-service-endpoints');

            let Service = TixService;
            let url = 'v3/book/form/combined';

            if (variant?.value === 'on') {
                Service = OrdersService;
                url = 'wtix/v4/book/form/combined';
            }

            const {
                result,
                meta,
            } = await Service.post(url, { body });

            const {
                form,
                orderId,
                summary,
            } = result;

            dispatch(bookingActions.setBookingForm({
                fields: addExtraFields(form.fields),
                meta,
            }));
            dispatch(bookingActions.setQuoteSummary(summary));
            dispatch(orderIdReceived({ orderId }));

            const localitiesUrl = meta?.dataSources?.localities;
            if (localitiesUrl) {
                const { countries } = await fetchLocalityData(localitiesUrl);
                dispatch(bookingActions.setLocalities({ countries }));
            }
        } catch (ex) {
            const domainUserInfo = appSettings.env.isClient
                ? SnowplowService.getDomainUserInfo()
                : {};

            logger.error('fetch', ex, {
                snowplowIds: {
                    networkUserId: SnowplowService.getNetworkUserId(),
                    ...domainUserInfo,
                },
            });
            error = {
                code: ex.code,
                message: ex.message,
                name: ex.name,
            };
        } finally {
            dispatch(actions.endWork(opts, actionMeta, error));
        }
    };
}

function reset(opts) {
    return actions.reset(opts);
}

const fetchFormAndSummaryAction = {
    fetch: fetchFormAndSummary,
    reset,
    ...selectors,
};

export {
    fetchFormAndSummaryAction,
};

export default {
    [namespace]: reducer,
};
